import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { AlertCircle, Play, CheckCircle, XSquare } from "lucide-react";
import type { ClientWithAudio } from "./types";
import {
  formatDuration,
  formatOptionalDuration,
  isFiniteNumber,
  median,
} from "./types";

interface ClientResultCardProps {
  client: ClientWithAudio;
  onPlayAudio: (client: ClientWithAudio) => void;
}

export const ClientResultCard = ({
  client,
  onPlayAudio,
}: ClientResultCardProps) => {
  const hasCapturedAudio = client.metrics.audioChunksReceived > 0;
  const ttfaMs = median(client.metrics.userPerceivedTtfaMsPerTurn);
  const audioGapMs = median(client.metrics.audioGapsMsPerTurn);
  const wsReadyWaitMs =
    client.metrics.wsReadyWaitMs ?? client.metrics.wsConnectTimeMs;
  const hasWsOpenMetric = isFiniteNumber(client.metrics.wsCreateToOpenMs);
  const hasStartToWsMetric = isFiniteNumber(
    client.metrics.startClickToWsCreateMs,
  );

  return (
    <Card
      className={`overflow-hidden ${client.metrics.success ? "border-l-2 border-l-primary" : "border-l-2 border-l-destructive"}`}
    >
      <CardContent className="p-0">
        <div className="flex justify-between items-center px-3 py-2 border-b">
          <span className="font-medium text-sm flex items-center gap-2">
            <div className="w-6 h-6 rounded-md bg-muted flex items-center justify-center text-xs font-medium">
              {client.client_id}
            </div>
            Client {client.client_id}
          </span>
          <Badge variant={client.metrics.success ? "secondary" : "destructive"}>
            {client.metrics.success ? (
              <>
                <CheckCircle className="h-3 w-3 mr-1" /> Success
              </>
            ) : (
              <>
                <XSquare className="h-3 w-3 mr-1" /> Failed
              </>
            )}
          </Badge>
        </div>
        <div className="p-3">
          <div className="grid grid-cols-2 gap-2.5">
            <div className="flex flex-col gap-0.5 p-2 rounded-md bg-muted">
              <span className="text-[0.65rem] text-muted-foreground uppercase tracking-wider">
                Login
              </span>
              <span className="text-sm font-semibold text-foreground">
                {client.metrics.timeToLoginMs > 0
                  ? formatDuration(client.metrics.timeToLoginMs)
                  : "N/A"}
              </span>
            </div>
            <div className="flex flex-col gap-0.5 p-2 rounded-md bg-muted">
              <span className="text-[0.65rem] text-muted-foreground uppercase tracking-wider">
                WS Ready
              </span>
              <span className="text-sm font-semibold text-foreground">
                {formatDuration(wsReadyWaitMs)}
              </span>
            </div>
            {hasWsOpenMetric && (
              <div className="flex flex-col gap-0.5 p-2 rounded-md bg-muted">
                <span className="text-[0.65rem] text-muted-foreground uppercase tracking-wider">
                  WS Open
                </span>
                <span className="text-sm font-semibold text-foreground">
                  {formatOptionalDuration(client.metrics.wsCreateToOpenMs)}
                </span>
              </div>
            )}
            {hasStartToWsMetric && (
              <div className="flex flex-col gap-0.5 p-2 rounded-md bg-muted">
                <span className="text-[0.65rem] text-muted-foreground uppercase tracking-wider">
                  Start to WS
                </span>
                <span className="text-sm font-semibold text-foreground">
                  {formatOptionalDuration(
                    client.metrics.startClickToWsCreateMs,
                  )}
                </span>
              </div>
            )}
            <div className="flex flex-col gap-0.5 p-2 rounded-md bg-muted">
              <span className="text-[0.65rem] text-muted-foreground uppercase tracking-wider">
                First Audio
              </span>
              <span className="text-sm font-semibold text-foreground">
                {hasCapturedAudio
                  ? formatDuration(client.metrics.timeToFirstAudioMs)
                  : "N/A"}
              </span>
            </div>
            <div className="flex flex-col gap-0.5 p-2 rounded-md bg-muted">
              <span className="text-[0.65rem] text-muted-foreground uppercase tracking-wider">
                Turns
              </span>
              <span className="text-sm font-semibold text-foreground">
                {client.metrics.turnsCompleted}/{client.metrics.turnsAttempted}
              </span>
            </div>
            <div className="flex flex-col gap-0.5 p-2 rounded-md bg-muted">
              <span className="text-[0.65rem] text-muted-foreground uppercase tracking-wider">
                Duration
              </span>
              <span className="text-sm font-semibold text-foreground">
                {formatDuration(client.metrics.totalDurationMs)}
              </span>
            </div>
            <div className="flex flex-col gap-0.5 p-2 rounded-md bg-muted">
              <span className="text-[0.65rem] text-muted-foreground uppercase tracking-wider">
                Perceived TTFA
              </span>
              <span className="text-sm font-semibold text-foreground">
                {ttfaMs != null ? formatDuration(ttfaMs) : "N/A"}
              </span>
            </div>
            <div className="flex flex-col gap-0.5 p-2 rounded-md bg-muted">
              <span className="text-[0.65rem] text-muted-foreground uppercase tracking-wider">
                Audio Gap
              </span>
              <span className="text-sm font-semibold text-foreground">
                {audioGapMs != null ? formatDuration(audioGapMs) : "N/A"}
              </span>
            </div>
          </div>
          {client.metrics.errors.length > 0 && (
            <div className="flex flex-col gap-1.5 mt-2">
              {client.metrics.errors.map((err, i) => (
                <div
                  key={i}
                  className="flex items-start gap-1.5 rounded-md bg-destructive px-2 py-1 text-xs font-normal text-white"
                >
                  <AlertCircle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                  <span className="min-w-0 break-words whitespace-pre-wrap">
                    {err}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="flex justify-end px-3 py-2 border-t bg-muted/30">
          {client.audio_url ? (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => onPlayAudio(client)}
              className="hover:bg-muted"
            >
              <Play className="h-3.5 w-3.5 mr-1.5" />
              Play Audio
            </Button>
          ) : (
            <span className="text-xs text-muted-foreground italic">
              No audio available
            </span>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
